/**
 * CoinManagementTab — Enable/disable coins globally.
 * Disabled coins are hidden from AddTab, DeleteTab, and grayed out in EditTab.
 * State persisted in ClickHouse (dashboard_meta.coin_config).
 */
import React, { useState, useMemo } from 'react';
import { useCoinRegistry } from '../../shared/CoinRegistryContext';

export default function CoinManagementTab() {
  const {
    availableCoins,
    disabledCoins,
    coinGenesis,
    coinColors,
    toggleCoin,
    bulkToggle,
    loading,
  } = useCoinRegistry();

  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search) return availableCoins;
    const q = search.toUpperCase();
    return availableCoins.filter((c) => c.includes(q));
  }, [availableCoins, search]);

  const enabledCount = availableCoins.filter(
    (c) => !disabledCoins.has(c),
  ).length;
  const disabledCount = disabledCoins.size;

  const handleEnableAll = () => bulkToggle(availableCoins, true);
  const handleDisableAll = () => bulkToggle(availableCoins, false);

  if (loading) {
    return <div className="cm-loading">Loading coins...</div>;
  }

  return (
    <div className="cm-root">
      {/* Header */}
      <div className="at-card">
        <div className="at-card-header">
          <span className="at-card-title">Coin Management</span>
          <div className="cm-header-stats">
            <span className="cm-stat enabled">{enabledCount} enabled</span>
            <span className="cm-stat disabled">{disabledCount} disabled</span>
          </div>
        </div>
        <div className="at-card-body">
          <div className="cm-sub">
            Toggle coins on or off. Disabled coins are hidden from the Add and
            Delete tabs, and grayed out in the Edit tab.
          </div>

          <div className="cm-controls">
            <input
              className="cm-search"
              placeholder="Search coins..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="cm-bulk-actions">
              <button className="cm-bulk-btn" onClick={handleEnableAll}>
                Enable All
              </button>
              <button
                className="cm-bulk-btn cm-bulk-btn--danger"
                onClick={handleDisableAll}
              >
                Disable All
              </button>
            </div>
          </div>

          {/* Coin grid */}
          <div className="cm-grid">
            {filtered.map((coin) => {
              const enabled = !disabledCoins.has(coin);
              const color = coinColors[coin] || '#8b949e';
              const genesis = coinGenesis[coin];

              return (
                <div
                  key={coin}
                  className={`cm-card ${enabled ? '' : 'cm-card--disabled'}`}
                >
                  <div className="cm-card-left">
                    <span
                      className="cm-color-dot"
                      style={{
                        background: color === '#000000' ? '#8b949e' : color,
                      }}
                    />
                    <div className="cm-card-info">
                      <span className="cm-card-symbol">{coin}</span>
                      {genesis && (
                        <span className="cm-card-genesis">Since {genesis}</span>
                      )}
                    </div>
                  </div>
                  <label className="cm-toggle">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={() => toggleCoin(coin, !enabled)}
                    />
                    <span className="cm-toggle-track">
                      <span className="cm-toggle-thumb" />
                    </span>
                  </label>
                </div>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="cm-empty">No coins match "{search}"</div>
          )}
        </div>
      </div>
    </div>
  );
}
